import React from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
  Box,
  TextField,
  Typography,
  Paper,
  Link,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Button } from "../components";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "../context/SnackbarContext";
import api from "../api/axiosInstance";
import Logo from "../assets/holding-hands.svg";

const signupSchema = Yup.object().shape({
  name: Yup.string().min(3, "Too short").required("Name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
});

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = React.useState(false);

  const handleSubmit = async (values: { name: string; email: string }) => {
    setLoading(true);
    try {
      await api.post("/auth/signup/", values);
      showSnackbar(
        "Signup successful! Check your email for your password, then log in.",
        "success"
      );
      setTimeout(() => navigate("/login"), 2500);
    } catch (e: any) {
      const errorMessage =
        e?.response?.data?.detail || 
        e?.response?.data?.message || 
        "Signup failed. Please try again.";
      showSnackbar(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        background: "linear-gradient(140deg, #eaf3ff 0%, #f5f8ff 100%)",
        p: 2,
      }}
    >
      <Paper
        elevation={5}
        sx={{
          width: { xs: "100%", sm: 450 },
          //   p: { xs: 3, sm: 5 },
          padding: "10px 30px 20px 30px",
          borderRadius: 5,
          backgroundColor: "#ffffff",
        }}
      >
        <Stack alignItems="center" textAlign="center">
          <Box
            component="img"
            // @ts-ignore
            src={Logo}
            alt="Organization logo"
            sx={{ height: 160 }}
          />
          <Typography variant="h4" fontWeight={600} sx={{ color: "#212B36" }}>
            Create your account
          </Typography>
          <Typography sx={{ color: "#637381", fontSize: 16, mb: 1 }}>
            We’ll email you a temporary password
          </Typography>
        </Stack>

        <Formik
          initialValues={{ name: "", email: "" }}
          validationSchema={signupSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched }) => (
            <Form noValidate>
              <Field
                as={TextField}
                fullWidth
                margin="normal"
                id="name"
                name="name"
                label="Full Name"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
              <Field
                as={TextField}
                fullWidth
                margin="normal"
                id="email"
                name="email"
                type="email"
                label="Email address"
                error={touched.email && Boolean(errors.email)}
                helperText={touched.email && errors.email}
              />
              <Button type="submit" fullWidth sx={{ mt: 3 }} disabled={loading}>
                {loading ? <CircularProgress size={24} /> : "Sign Up"}
              </Button>
            </Form>
          )}
        </Formik>

        <Typography align="center" mt={2} sx={{ color: "#4B5563" }}>
          Already have an account?{" "}
          <Link
            component={RouterLink}
            to="/login"
            underline="hover"
            sx={{ color: "#1A73E8", fontWeight: 600 }}
          >
            Sign in instead
          </Link>
        </Typography>
      </Paper>
    </Box>
  );
};

export default Signup;
